<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import LoadableButton from '@/components/common/atoms/loadable-button.vue';
import PersonalSurveyProfileForm from '@/components/settings/personal-survey-profile-form.vue';
import type {
  AdditionalBusinessContextSuggestionRefreshFrom,
  PersonalSurveyProfile,
  PersonalSurveyProfileInput,
} from '@/types';
import {
  hasBusinessInfo,
  isSameProfileInput,
  toPersonalSurveyProfileInput,
} from '@/utils/personalSurveyProfile';
import { featureFlags } from '@/featureFlags';

const props = defineProps<{
  variant: 'adjust' | 'register';
  profile: PersonalSurveyProfile;
  isSubmitting: boolean;
  hasSaveError: boolean;
}>();
const emit = defineEmits<{
  save: [input: PersonalSurveyProfileInput];
  suggestionApply: [];
  suggestionRefresh: [from: AdditionalBusinessContextSuggestionRefreshFrom];
}>();
const open = defineModel<boolean>('open', { default: false });

const initialInput = ref(toPersonalSurveyProfileInput(props.profile));
const input = ref(toPersonalSurveyProfileInput(props.profile));
const formValid = ref(true);

watch(open, opened => {
  if (!opened) return;
  initialInput.value = toPersonalSurveyProfileInput(props.profile);
  input.value = toPersonalSurveyProfileInput(props.profile);
});

const isSubmitDisabled = computed(
  () =>
    props.isSubmitting ||
    !formValid.value ||
    !hasBusinessInfo(input.value) ||
    isSameProfileInput(input.value, initialInput.value),
);

const showsSuggestion =
  featureFlags.ANDEV_10700_INSIGHT_ADDITIONAL_BUSINESS_CONTEXT &&
  featureFlags.ANDEV_10813_INSIGHT_ADDITIONAL_BUSINESS_CONTEXT_SUGGESTION;

const closeInterceptor = () => !props.isSubmitting;

const submit = () => {
  if (isSubmitDisabled.value) return;
  emit('save', input.value);
};
</script>

<template>
  <DgrDialog
    v-model:open="open"
    :with-header-divider="false"
    :close-interceptor-handler="closeInterceptor"
  >
    <template #description>
      <div class="content" :class="{ wide: showsSuggestion }">
        <div class="header">
          <div class="c-title-bold--xm">
            {{ variant === 'adjust' ? 'ヒントを調整' : '業務情報を登録' }}
          </div>
          <div v-if="variant === 'adjust'" class="c-text-regular--s subheader">
            業務情報をもとに、発想のヒントを配信します。<br />
            ここで変更した業務情報は、プロフィールにも反映されます。
          </div>
        </div>
        <div class="form">
          <PersonalSurveyProfileForm
            v-model="input"
            v-model:valid="formValid"
            with-additional-business-context-suggestion
            @suggestion-apply="emit('suggestionApply')"
            @suggestion-refresh="emit('suggestionRefresh', $event)"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="dialog-footer c-text-regular--m">
        <p v-if="hasSaveError" class="error-message c-text-regular--s">
          保存できませんでした。もう一度お試しください。
        </p>
        <button
          type="button"
          class="c-btn c-btn--auto c-btnOutline"
          :class="{ disabled: isSubmitting }"
          :disabled="isSubmitting"
          @click="open = false"
        >
          キャンセル
        </button>
        <LoadableButton
          type="button"
          class="c-btn c-btn--auto c-btn--AnewsPrimary"
          :class="{ disabled: isSubmitDisabled }"
          :disabled="isSubmitDisabled"
          :is-loading="isSubmitting"
          @click="submit"
        >
          保存して発想のヒントを生成する
        </LoadableButton>
      </div>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 560px;

  &.wide {
    width: 752px;
    max-width: 100%;
  }
}

.subheader {
  margin-top: 4px;
  color: $color-gray600;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;

  .error-message {
    margin: 0 auto 0 0;
    color: $color-orange1000;
  }
}
</style>
